import { define } from "@shared/Lib/registry";
import { Component } from "@shared/Lib/types";
import "./Overlay.scss";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  'input:not([disabled]):not([type="hidden"])',
  "select:not([disabled])",
  "textarea:not([disabled])",
  "iframe",
  "audio[controls]",
  "video[controls]",
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

const BODY_LOCK_CLASS = "overlay-locked";

export class Overlay implements Component {
  readonly el: HTMLElement;
  private dialog: HTMLElement | null = null;
  private isOpen = false;
  private previouslyFocused: HTMLElement | null = null;
  private focusableElements: HTMLElement[] = [];
  private firstFocusable: HTMLElement | null = null;
  private lastFocusable: HTMLElement | null = null;
  private boundDocumentClick = this.handleDocumentClick.bind(this);
  private boundKeydown = this.handleKeydown.bind(this);
  private boundFocusIn = this.handleFocusIn.bind(this);

  constructor(el: HTMLElement) {
    this.el = el;
    this.setup();
  }

  private setup(): void {
    if (!this.el.id) {
      this.el.id = `overlay-${Math.random().toString(36).slice(2, 9)}`;
    }

    this.dialog = this.el.querySelector<HTMLElement>("[data-overlay-body]");

    document.addEventListener("click", this.boundDocumentClick);
    document.addEventListener("keydown", this.boundKeydown);
    document.addEventListener("focusin", this.boundFocusIn);
    this.el.hidden = true;
  }

  private open(): void {
    if (this.isOpen) {
      return;
    }

    this.previouslyFocused = document.activeElement as HTMLElement;
    this.isOpen = true;
    this.el.classList.add("is-open");
    this.el.hidden = false;
    document.body.classList.add(BODY_LOCK_CLASS);

    this.cacheFocusableElements();
    (this.firstFocusable || this.dialog)?.focus();
  }

  private close(): void {
    if (!this.isOpen) {
      return;
    }

    this.isOpen = false;
    this.el.classList.remove("is-open");
    this.el.hidden = true;

    if (!document.querySelector(".syz_overlay.is-open")) {
      document.body.classList.remove(BODY_LOCK_CLASS);
    }

    if (this.previouslyFocused?.isConnected) {
      this.previouslyFocused.focus();
    }
  }

  private cacheFocusableElements(): void {
    const elements =
      this.dialog?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR) ?? [];
    this.focusableElements = Array.from(elements).filter(
      (node) => node.offsetParent !== null || node === document.activeElement
    );

    // Use `.at()` to avoid `undefined` with `noUncheckedIndexedAccess` / `exactOptionalPropertyTypes`
    this.firstFocusable = this.focusableElements.at(0) ?? null;
    this.lastFocusable = this.focusableElements.at(-1) ?? null;
  }

  private handleDocumentClick(event: Event): void {
    const target = (event.target as HTMLElement | null)?.closest<HTMLElement>(
      "[data-overlay-open], [data-overlay-close]"
    );
    if (!target) {
      return;
    }

    const openAttr = target.getAttribute("data-overlay-open");
    const closeAttr = target.getAttribute("data-overlay-close");
    const matchesOverlay = (attrValue: string | null): boolean => {
      if (!attrValue || attrValue === "" || attrValue === "#") {
        return this.el.contains(target);
      }

      const normalized = attrValue.startsWith("#")
        ? attrValue
        : `#${attrValue}`;
      return normalized === `#${this.el.id}`;
    };

    if (openAttr !== null && matchesOverlay(openAttr)) {
      event.preventDefault();
      this.open();
      return;
    }

    if (
      closeAttr !== null &&
      (matchesOverlay(closeAttr) || this.el.contains(target))
    ) {
      event.preventDefault();
      this.close();
    }
  }

  private handleKeydown(event: KeyboardEvent): void {
    if (!this.isOpen) {
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.key !== "Tab") {
      return;
    }

    this.cacheFocusableElements();
    if (!this.firstFocusable || !this.lastFocusable) {
      event.preventDefault();
      this.dialog?.focus();
      return;
    }

    if (event.shiftKey && document.activeElement === this.firstFocusable) {
      event.preventDefault();
      this.lastFocusable.focus();
    } else if (
      !event.shiftKey &&
      document.activeElement === this.lastFocusable
    ) {
      event.preventDefault();
      this.firstFocusable.focus();
    }
  }

  private handleFocusIn(event: FocusEvent): void {
    if (!this.isOpen) {
      return;
    }

    if (this.el.contains(event.target as Node)) {
      return;
    }

    (this.firstFocusable || this.dialog)?.focus();
  }

  destroy(): void {
    document.removeEventListener("click", this.boundDocumentClick);
    document.removeEventListener("keydown", this.boundKeydown);
    document.removeEventListener("focusin", this.boundFocusIn);
  }
}

define("Overlay", (el) => new Overlay(el));
